import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { useProductDetailContext } from "../context/ProductDetailContext";

import SubProductForm from "./SubProductForm";

import FormDialog from "@/components/reusables/FormDialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Button_Names } from "@/constants/global-constants";
import { ToolsConstants } from "@/constants/tools.constant";
import { useMutateSubProductsMutation } from "@/redux/api/toolsQuery";
import {
  TAddSubProduct,
  createSubProductSchema,
  defaultSubProduct,
} from "@/schemas/tools.schema";
import {
  filterEmptyValues,
  parseToFormData,
  validateResponse,
} from "@/utils/helper-functions/form.helper";

interface AddSubProductFormModalProps {
  isOpen: boolean;
  toggleAddSubProductModal: () => void;
}

const AddSubProductFormModal = ({
  isOpen,
  toggleAddSubProductModal,
}: AddSubProductFormModalProps): JSX.Element => {
  const { productId } = useProductDetailContext();
  const [createSubProduct, { isLoading }] = useMutateSubProductsMutation();

  const form = useForm<TAddSubProduct>({
    resolver: zodResolver(createSubProductSchema),
    defaultValues: defaultSubProduct,
  });

  const onSubmit = async (data: TAddSubProduct): Promise<void> => {
    const response = await createSubProduct({
      method: "POST",
      data: parseToFormData(filterEmptyValues(data), ["files", "images"]),
      productId,
    });
    validateResponse({
      response,
      onSuccess: () => {
        form.reset();
        toggleAddSubProductModal();
        toast.success(
          ToolsConstants.FORM.SUB_PRODUCT.CREATED_SUB_PRODUCT_SUCCESS,
        );
      },
    });
  };

  return (
    <FormDialog
      isOpen={isOpen}
      title={ToolsConstants.PRODUCTS.ADD_SUB_PRODUCT}
      toggleModal={toggleAddSubProductModal}
      isFormDirty={form.formState.isDirty}
      className='px-3'
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-8'>
          <SubProductForm />
          <div className=' flex justify-between'>
            <Button
              variant={"ghost"}
              onClick={() => form.reset()}
              disabled={isLoading}
            >
              {Button_Names.Reset}
            </Button>
            <Button type='submit' className='w-[200px]' isLoading={isLoading}>
              {ToolsConstants.FORM.SUB_PRODUCT.SAVE_SUB_PRODUCT}
            </Button>
          </div>
        </form>
      </Form>
    </FormDialog>
  );
};

export default AddSubProductFormModal;
